import React from 'react'
import { Typography } from '@mui/material'
import Box from '@src/components/Box'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { Brand, PricingAvailabilityItem } from '@src/aem-content/aem-types'
import { productDetailRoute } from '@src/routes'
import { Link } from '@src/components/Link'
import DiscoPromoProductPricing from './DiscoPromoMaterialPricing'

type DiscoPromoProductProps = {
  product?: string
  productsku?: string
  productName?: string | null
  productDescription?: string | null
  materialPricing: PricingAvailabilityItem[]
  dchainMessage?: string
  brand?: Brand | null
}

const useStyles = makeStyles((theme: Theme) => ({
  productContainer: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      flexDirection: 'column',
    },
  },
  productRow: {
    display: 'flex',
    alignItems: 'center',
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightRegular,
    borderTop: `1px solid ${theme.palette.grey[400]}`,
    backgroundColor: theme.palette.background.grey,
    [theme.breakpoints.up('md')]: {
      padding: theme.spacing(4, 0),
      height: '52px',
    },
    '& > div': {
      padding: theme.spacing(0, 2),
      lineHeight: 1.5,
    },
  },
  productNumber: {
    [theme.breakpoints.up('md')]: {
      width: '12.5%',
      minWidth: 120,
    },
  },

  productNumberText: {
    [theme.breakpoints.up('md')]: {
      fontSize: theme.typography.pxToRem(14),
    },
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },

  productName: {
    [theme.breakpoints.up('md')]: {
      width: '21%',
      minWidth: 180,
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
  },
  productNameText: {
    [theme.breakpoints.up('md')]: {
      fontSize: theme.typography.pxToRem(14),
      color: theme.palette.primary.main,
      fontWeight: theme.typography.fontWeightBold,
    },
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.dark,
    },
  },
  description: {
    [theme.breakpoints.up('md')]: {
      width: '45.5%',
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
    },
  },
  descriptionText: {
    [theme.breakpoints.up('md')]: {
      fontSize: theme.typography.pxToRem(14),
    },
    cursor: 'pointer',
    '&:hover': {
      color: theme.palette.primary.main,
    },
  },
}))

const DiscoPromoProduct = ({
  product,
  productsku,
  productName,
  materialPricing,
  dchainMessage,
  productDescription,
  brand,
}: DiscoPromoProductProps) => {
  const classes = useStyles()

  const productInfoContent = (
    className: string,
    contentValue?: string,
    brandKey?: string,
    product?: string,
    productsku?: string
  ) => {
    const content = (
      <Typography
        component="span"
        className={className}
        dangerouslySetInnerHTML={{ __html: contentValue ?? productsku ?? '' }}
      />
    )

    return brandKey && product ? (
      <Link {...productDetailRoute.index(brandKey, product)}>
        <a>{content}</a>
      </Link>
    ) : (
      content
    )
  }

  return (
    <Box key={product} className={classes.productContainer}>
      <Box className={classes.productRow}>
        <Box className={classes.productNumber}>
          {productInfoContent(
            classes.productNumberText,
            product,
            brand?.key,
            product,
            productsku
          )}
        </Box>
        <Box className={classes.productName}>
          {productInfoContent(
            classes.productNameText,
            productName ?? '',
            brand?.key,
            product
          )}
        </Box>
        <Box className={classes.description}>
          {productInfoContent(
            classes.descriptionText,
            productDescription ?? '',
            brand?.key,
            product
          )}
        </Box>
      </Box>

      <DiscoPromoProductPricing
        product={product}
        materialPricing={materialPricing}
        dchainMessage={dchainMessage}
      />
    </Box>
  )
}

export default DiscoPromoProduct
